import React, { useState, useEffect } from 'react';
import { format, startOfWeek, addDays, startOfDay, isSameDay } from 'date-fns';

interface Event {
  id: string;
  title: string;
  start: Date;
  end: Date;
  type: 'talk' | 'workshop' | 'networking' | 'break';
  location?: string;
  speakers?: Array<{ id: string; name: string }>;
}

interface ConferenceCalendarProps {
  events: Event[];
  onEventClick?: (event: Event) => void;
  isEditable?: boolean;
  onEventUpdate?: (event: Event) => void;
}

const ConferenceCalendar: React.FC<ConferenceCalendarProps> = ({
  events,
  onEventClick,
  isEditable = false,
  onEventUpdate
}) => {
  // Find the earliest event date to set as initial date
  const initialDate = events.length > 0 
    ? events.reduce((earliest, event) => 
        event.start < earliest ? event.start : earliest, 
        events[0].start)
    : new Date();

  const [currentDate, setCurrentDate] = useState(initialDate);

  useEffect(() => {
    // console.log('Calendar received events:', events.map(e => ({
    //   id: e.id,
    //   title: e.title,
    //   type: e.type,
    //   start: e.start.toISOString()
    // })));
  }, [events]);

  const generateTimeSlots = () => {
    const slots = [];
    for (let i = 8; i < 20; i++) {
      const hour = i < 10 ? `0${i}` : `${i}`;
      slots.push(`${hour}:00`);
      slots.push(`${hour}:30`);
    }
    return slots;
  };

  const generateWeekDays = (start: Date) => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(start, i));
    }
    return days;
  };

  const timeSlots = generateTimeSlots();
  const weekStart = startOfWeek(currentDate);
  const weekDays = generateWeekDays(weekStart);

  const getEventsForTimeSlot = (day: Date, time: string) => {
    const [slotHour, slotMinutes] = time.split(':').map(Number);
    const slotTime = new Date(day);
    slotTime.setHours(slotHour);
    slotTime.setMinutes(slotMinutes);
    slotTime.setSeconds(0);
    slotTime.setMilliseconds(0);

    // Get events that start in this slot
    return events.filter(event => {
      const eventStart = new Date(event.start);
      
      // Check if event starts in this slot
      return eventStart.getFullYear() === slotTime.getFullYear() &&
             eventStart.getMonth() === slotTime.getMonth() &&
             eventStart.getDate() === slotTime.getDate() &&
             eventStart.getHours() === slotHour &&
             (slotMinutes === 0 ? 
               eventStart.getMinutes() < 30 : 
               eventStart.getMinutes() >= 30);
    });
  };

  const getEventStyle = (event: Event) => {
    const eventStart = new Date(event.start);
    const eventEnd = new Date(event.end);
    
    // Calculate start position
    const startMinutes = eventStart.getMinutes();
    const slotStartMinutes = startMinutes >= 30 ? 30 : 0;
    const positionInSlot = ((startMinutes - slotStartMinutes) / 30) * 100;
    
    // Calculate duration and height
    const durationMinutes = (eventEnd.getTime() - eventStart.getTime()) / (1000 * 60);
    const heightInSlots = (durationMinutes / 30) * 48; // 48px is the height of a slot
    
    return {
      position: 'absolute' as const,
      top: `${positionInSlot}%`,
      height: `${heightInSlots}px`,
      left: '4px',
      right: '4px',
      zIndex: 1,
      maxHeight: 'none',
      overflow: 'hidden'
    };
  };

  const getEventClassName = (event: Event) => {
    const baseClass = 'absolute rounded-lg text-xs p-1 cursor-pointer transition-all duration-200';
    
    switch (event.type) {
      case 'talk':
        return `${baseClass} bg-blue-100 text-blue-800 hover:bg-blue-200`;
      case 'workshop':
        return `${baseClass} bg-green-100 text-green-800 hover:bg-green-200`;
      case 'networking':
        return `${baseClass} bg-purple-100 text-purple-800 hover:bg-purple-200`;
      case 'break':
        return `${baseClass} bg-gray-100 text-gray-800 hover:bg-gray-200`;
      default:
        return baseClass;
    }
  };

  const handleEventClick = (event: Event) => {
    if (onEventClick) {
      onEventClick(event);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Calendar Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex space-x-4">
          <button
            onClick={() => setCurrentDate(addDays(currentDate, -7))}
            className="p-2 hover:bg-gray-100 rounded"
          >
            ←
          </button>
          <h2 className="text-lg font-semibold">
            {format(weekStart, 'MMMM d, yyyy')}
          </h2>
          <button
            onClick={() => setCurrentDate(addDays(currentDate, 7))}
            className="p-2 hover:bg-gray-100 rounded"
          >
            →
          </button>
        </div>
        <div className="flex space-x-2">
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-blue-500 mr-1"></div>
            <span className="text-sm">Talk</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-green-500 mr-1"></div>
            <span className="text-sm">Workshop</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-purple-500 mr-1"></div>
            <span className="text-sm">Networking</span>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="flex">
        {/* Time slots column */}
        <div className="w-20 border-r border-gray-200">
          <div className="h-12 border-b border-gray-200"></div>
          {timeSlots.map((time) => (
            <div key={time} className="h-12 border-b border-gray-200 px-2 py-1">
              <span className="text-xs text-gray-500">{time}</span>
            </div>
          ))}
        </div>

        {/* Days columns */}
        {weekDays.map((day) => (
          <div key={format(day, 'yyyy-MM-dd')} className="flex-1 min-w-0">
            <div className="h-12 border-b border-gray-200 px-2 py-1 text-center">
              <div className="font-semibold">{format(day, 'EEE')}</div>
              <div className="text-sm text-gray-500">{format(day, 'd')}</div>
            </div>
            {timeSlots.map((time) => {
              const slotEvents = getEventsForTimeSlot(day, time);
              return (
                <div
                  key={`${format(day, 'yyyy-MM-dd')}-${time}`}
                  className="h-12 border-b border-gray-200 px-1 relative"
                  style={{ minHeight: '48px' }}
                >
                  {slotEvents.map((event) => (
                    <div
                      key={event.id}
                      className={getEventClassName(event)}
                      style={getEventStyle(event)}
                      onClick={() => handleEventClick(event)}
                    >
                      <div className="font-medium truncate">{event.title}</div>
                      {event.location && (
                        <div className="text-xs truncate">{event.location}</div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ConferenceCalendar;
